/**
 * Select — single-select dropdown that opens a BottomSheet.
 *
 * Trigger renders like a TextInput field. On press, opens a sheet
 * with a scrollable list of options. Supports search, error state,
 * disabled options, and selected checkmark indicator.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §12
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { selection } from '../../lib/utils/haptics'
import { Platform, View, Text, TextInput, ScrollView as RNScrollView } from 'react-native'
import { AppModal } from './AppModal'
import { GlassSheet } from './GlassSheet'
import { ChevronDown, Check } from '@/components/ui/icons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Pressable } from './Pressable'
import { TrackedNativePressable as RNPressable } from './TrackedNativePressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import {
  useSemanticColors,
  FONTS,
  RADIUS,
  SPACING,
  FORM_FIELD_GAP,
  INTERACTION,
} from '../../lib/theme/tokens'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SelectOption<T extends string = string> {
  value: T
  label: string
  description?: string
  icon?: React.ReactNode
  disabled?: boolean
}

interface SelectProps<T extends string = string> {
  /** Currently selected value */
  value: T | null
  /** Called when user selects an option */
  onChange: (value: T) => void
  /** List of options */
  options: SelectOption<T>[]
  /** Placeholder text (shown when value is null) */
  placeholder?: string
  /** Field label */
  label?: string
  /** Error message */
  error?: string
  /** Disabled state */
  disabled?: boolean
  /** Whether to show a search input inside the bottom sheet */
  searchable?: boolean
  /** Title for the bottom sheet */
  sheetTitle?: string
  /** Additional NativeWind className */
  className?: string
  /** Test ID for E2E automation */
  testID?: string
}

// ─── Select ─────────────────────────────────────────────────────────────────

function normalizeSearchValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export function Select<T extends string = string>({
  value,
  onChange,
  options,
  placeholder,
  label,
  error,
  disabled = false,
  searchable = false,
  sheetTitle,
  className,
  testID,
}: SelectProps<T>) {
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t('ui.select_placeholder')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // Same contract as TextInput: the field floor moves with its own text.
  const { box } = useFontScale()
  const insets = useSafeAreaInsets()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const searchInputRef = useRef<React.ElementRef<typeof TextInput>>(null)

  const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value])

  const filteredOptions = useMemo(() => {
    const q = normalizeSearchValue(search)
    if (!q) return options
    return options.filter((o) => normalizeSearchValue(o.label).includes(q))
  }, [options, search])

  useEffect(() => {
    if (!isOpen || !searchable) return undefined

    const timeout = setTimeout(() => searchInputRef.current?.focus(), 150)
    return () => clearTimeout(timeout)
  }, [isOpen, searchable])

  const handleOpen = useCallback(() => {
    if (disabled) return
    setSearch('')
    setIsOpen(true)
  }, [disabled])

  const handleSelect = useCallback(
    (option: SelectOption<T>) => {
      if (option.disabled) return
      selection()
      onChange(option.value)
      // Delay close to let parent process onChange before Modal unmounts —
      // prevents state race that blocks reopening on native
      requestAnimationFrame(() => {
        setIsOpen(false)
      })
    },
    [onChange],
  )

  return (
    <View className={cn(className)} style={{ marginBottom: FORM_FIELD_GAP }}>
      {label ? (
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            color: colors.textPrimary,
            opacity: 0.75,
            // 10 → 16px label-to-input gap, matching TextInput / DatePicker.
            marginBottom: SPACING.md,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </Text>
      ) : null}

      <Pressable
        onPress={handleOpen}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label ?? resolvedPlaceholder}
        accessibilityState={{ disabled, expanded: isOpen }}
        testID={testID}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: box(INTERACTION.fieldHeight),
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.md,
          borderRadius: RADIUS.md,
          borderWidth: 1,
          borderColor: error ? colors.error : colors.border,
          // Same token TextInput and DatePicker paint their field with, on
          // purpose: these three stack as sibling rows in the same form on 16
          // of this component's 17 call sites, so the fill has to be one token
          // or the rows stop looking like one control family.
          //
          // NOTE the fill is weak in dark on the app background —
          // `surfaceSubtle` is rgba(255,255,255,0.04), which composites to
          // #1A1724 over #110E1C, 1.08:1, so the field reads as a bare
          // outline. That is the token, not this component: TextInput on the
          // same background samples the identical #1A1724. It only looks
          // solved on cards because 4% white over `surface` (#242140) happens
          // to land exactly on `surfaceRaised` (#2C2947). Fixing it here alone
          // would split the form rows; it belongs in tokens.ts.
          backgroundColor: colors.surfaceSubtle,
          opacity: disabled ? 0.5 : pressed ? 0.76 : 1,
        })}
      >
        <Text
          style={{
            flex: 1,
            // The value/placeholder was the only text in this file without a
            // font, so it fell back to the platform default while the label
            // above it and every TextInput beside it rendered Montserrat.
            // Same three values TextInput uses for its input: FONTS.body / 15
            // / textPrimary, with textMuted standing in for the placeholder.
            fontFamily: FONTS.body,
            fontSize: 15,
            color: selectedOption ? colors.textPrimary : colors.textMuted,
          }}
          numberOfLines={1}
        >
          {selectedOption?.label ?? resolvedPlaceholder}
        </Text>
        <ChevronDown size={18} color={colors.textSecondary} />
      </Pressable>

      {error ? (
        <Text
          style={{
            fontSize: 12,
            color: colors.textError,
            marginTop: SPACING.xs,
            fontFamily: FONTS.body,
          }}
        >
          {error}
        </Text>
      ) : null}

      {/* AppModal, never a raw react-native Modal: it carries the
          GestureHandlerRootView the options list needs to be tappable on
          Android. The RN built-in Pressable/ScrollView below are a deliberate
          choice for the list, not a workaround for that. */}
      <AppModal
        visible={isOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setIsOpen(false)}
      >
        {/* AppModal owns keyboard avoidance for every modal window.
            The dimmer lives in AppModal (mask fade, never a slide). */}
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <RNPressable
            onPress={() => setIsOpen(false)}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          />
          <GlassSheet
            style={{
              borderTopLeftRadius: RADIUS.xl,
              borderTopRightRadius: RADIUS.xl,
              maxHeight: '60%',
            }}
            contentStyle={{
              paddingBottom: insets.bottom + SPACING.lg,
            }}
          >
            {/* Handle bar */}
            <View style={{ alignItems: 'center', paddingVertical: SPACING.sm + SPACING.xs }}>
              <View
                style={{
                  width: 36,
                  height: 4,
                  borderRadius: RADIUS.xs,
                  backgroundColor: colors.border,
                }}
              />
            </View>
            {sheetTitle ? (
              <Text
                style={{
                  fontSize: 16,
                  color: colors.textPrimary,
                  paddingHorizontal: SPACING.md,
                  paddingBottom: 8,
                  fontFamily: FONTS.body,
                }}
              >
                {sheetTitle}
              </Text>
            ) : null}
            {searchable && (
              <View style={{ paddingHorizontal: SPACING.md, paddingVertical: SPACING.sm }}>
                <TextInput
                  ref={searchInputRef}
                  value={search}
                  onChangeText={setSearch}
                  placeholder={t('ui.select_search')}
                  placeholderTextColor={colors.textMuted}
                  style={{
                    height: 40,
                    paddingHorizontal: SPACING.sm + SPACING.xs,
                    borderRadius: RADIUS.sm,
                    borderWidth: 1,
                    borderColor: colors.border,
                    // Was `isDark ? surface : background` — the one field in
                    // the app painted with neither field token, and in dark it
                    // recessed (#242140 under a #2C2947 sheet) while every
                    // other field lifts. `surfaceSubtle` is the field fill, so
                    // it lifts on the sheet in both themes and inherits any
                    // future repair of that token along with its siblings.
                    backgroundColor: colors.surfaceSubtle,
                    color: colors.textPrimary,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                  }}
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoFocus
                  blurOnSubmit={false}
                  returnKeyType="search"
                  onSubmitEditing={() => {
                    const firstEnabledOption = filteredOptions.find((item) => !item.disabled)
                    if (firstEnabledOption) handleSelect(firstEnabledOption)
                  }}
                />
              </View>
            )}
            {/*
              `flexShrink: 1`: el padre limita a `maxHeight: '60%'`, pero un
              ScrollView sin restriccion se mide por su contenido y decide que
              no hay nada que desplazar. Una lista larga de opciones queda
              cortada y sin salida.
            */}
            <RNScrollView
              style={{ flexShrink: 1, minHeight: searchable ? INTERACTION.minTouchTarget : 0 }}
              bounces={false}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            >
              {filteredOptions.map((item) => {
                const isSelected = item.value === value
                return (
                  <RNPressable
                    key={item.value}
                    onPress={() => handleSelect(item)}
                    disabled={item.disabled}
                    testID={testID ? `${testID}.option-${item.value}` : undefined}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.md - 2,
                      opacity: item.disabled ? 0.4 : 1,
                    }}
                    accessibilityRole="menuitem"
                    accessibilityState={{ selected: isSelected, disabled: item.disabled }}
                  >
                    {item.icon ? (
                      <View style={{ marginRight: SPACING.sm + SPACING.xs }}>{item.icon}</View>
                    ) : null}
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: 15,
                          fontFamily: FONTS.body,
                          color: colors.textPrimary,
                        }}
                      >
                        {item.label}
                      </Text>
                      {item.description ? (
                        <Text
                          style={{
                            fontSize: 13,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                            marginTop: 2,
                          }}
                        >
                          {item.description}
                        </Text>
                      ) : null}
                    </View>
                    {isSelected && <Check size={20} color={colors.primary} strokeWidth={2.5} />}
                  </RNPressable>
                )
              })}
              {filteredOptions.length === 0 && (
                <View style={{ paddingHorizontal: SPACING.md, paddingVertical: SPACING.lg }}>
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONTS.body,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('ui.select_no_results')}
                  </Text>
                </View>
              )}
            </RNScrollView>
          </GlassSheet>
        </View>
      </AppModal>
    </View>
  )
}
